import { useCallback, useEffect, useMemo, useState } from 'react';
import { MdSearch, MdPersonAdd, MdRefresh, MdInbox } from 'react-icons/md';
import { Badge, Button, Checkbox, EmptyState, FilterChip, SkeletonList, Toolbar } from '../ui';
import { importCommit, importPreview } from '../../api/addressBook';
import { messaggioErrore } from '../../utils/apiError';

const MOSTRATI = 150;
// Molti contatti WhatsApp si chiamano come il proprio numero: distinguere
// "ha un nome vero" da "è solo un numero" è il filtro che serve davvero.
const soloNumero = nome => !nome || /^[\d\s+() -]+$/.test(nome);

/**
 * Elenco persistente di chi ti scrive ma non è ancora in rubrica.
 * Serve ad aggiungerne dieci in due clic, non a importarne mille.
 */
export default function CandidatesPanel({ connection, onImported }) {
  const [dati, setDati] = useState(null);
  const [caricamento, setCaricamento] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errore, setErrore] = useState('');
  const [query, setQuery] = useState('');
  const [scelti, setScelti] = useState([]);
  const [tipo, setTipo] = useState('person');
  const [conNome, setConNome] = useState(true);
  const [conNumeri, setConNumeri] = useState(false);

  const carica = useCallback(async (numeri) => {
    if (!connection) return;
    setCaricamento(true); setErrore(''); setScelti([]);
    try {
      setDati(await importPreview({ connection: Number(connection), with_numbers: !!numeri }));
    } catch (e) {
      setErrore(messaggioErrore(e));
    } finally {
      setCaricamento(false);
    }
  }, [connection]);

  useEffect(() => { carica(false); }, [carica]);

  const visibili = useMemo(() => {
    if (!dati) return [];
    const q = query.trim().toLocaleLowerCase();
    return dati.candidates.filter(c => (
      !c.already
      && c.kind === tipo
      && (tipo !== 'person' || !conNome || !soloNumero(c.name))
      && (!q || `${c.name} ${c.whatsapp_id} ${c.phone || ''}`.toLocaleLowerCase().includes(q))
    ));
  }, [dati, query, tipo, conNome]);

  const selezionati = new Set(scelti);

  const aggiungi = async () => {
    setBusy(true); setErrore('');
    try {
      const esito = await importCommit({ connection: Number(connection), whatsapp_ids: scelti });
      setScelti([]);
      await carica(conNumeri);
      onImported?.(esito);
    } catch (e) {
      setErrore(messaggioErrore(e));
    } finally {
      setBusy(false);
    }
  };

  if (caricamento && !dati) return <SkeletonList rows={5} />;

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted">
        Chi ti ha scritto ma non è ancora in rubrica. Spunta solo quelli che ti servono:
        nessuno viene aggiunto finché non premi il pulsante.
        {tipo === 'group' && ' Un gruppo aggiunto qui serve a pescarne i membri: lo apri dalla rubrica e scegli chi vuoi.'}
      </p>

      {errore && <p role="alert" className="rounded bg-danger-bg px-3 py-2 text-sm text-danger">{errore}</p>}

      <Toolbar className="flex-col items-stretch gap-2 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <MdSearch size={18} aria-hidden className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-muted" />
          <input aria-label="Cerca fra i contatti da aggiungere" placeholder="Cerca nome o numero"
            value={query} onChange={e => setQuery(e.target.value)}
            className="w-full rounded border border-line bg-surface py-2 pl-8 pr-3 text-base md:text-sm" />
        </div>
        <Button size="sm" icon={MdRefresh} loading={caricamento}
          onClick={() => { setConNumeri(true); carica(true); }}>
          Recupera i numeri
        </Button>
      </Toolbar>

      <div className="flex flex-wrap gap-2">
        <FilterChip active={tipo === 'person'} onClick={() => { setTipo('person'); setScelti([]); }}>
          Persone
        </FilterChip>
        <FilterChip active={tipo === 'group'} onClick={() => { setTipo('group'); setScelti([]); }}>
          Gruppi WhatsApp
        </FilterChip>
        {tipo === 'person' && (
          <FilterChip active={conNome} onClick={() => setConNome(!conNome)}>
            Solo con un nome
          </FilterChip>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-ink" aria-live="polite">
          {scelti.length > 0
            ? <><strong>{scelti.length}</strong> selezionati su {visibili.length}</>
            : <>{visibili.length} da aggiungere</>}
        </p>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" disabled={!visibili.length}
            onClick={() => setScelti([...new Set([...scelti, ...visibili.map(c => c.whatsapp_id)])])}>
            Seleziona tutti i filtrati
          </Button>
          <Button size="sm" disabled={!scelti.length} onClick={() => setScelti([])}>Deseleziona</Button>
          <Button variant="primary" size="sm" icon={MdPersonAdd} loading={busy}
            disabled={!scelti.length} onClick={aggiungi}>
            Aggiungi {scelti.length || ''} alla rubrica
          </Button>
        </div>
      </div>

      {!visibili.length ? (
        <EmptyState icon={MdInbox} title="Nessuno da aggiungere"
          hint={conNome && tipo === 'person'
            ? 'Con questi filtri non resta nessuno: prova a togliere "Solo con un nome".'
            : 'Tutti quelli che ti hanno scritto sono già in rubrica.'} />
      ) : (
        <ul className="overflow-hidden rounded-lg border border-line bg-surface">
          {visibili.slice(0, MOSTRATI).map(c => (
            <li key={c.whatsapp_id} className="border-b border-line px-3 py-2 last:border-0">
              <Checkbox
                checked={selezionati.has(c.whatsapp_id)}
                onChange={e => setScelti(e.target.checked
                  ? [...scelti, c.whatsapp_id]
                  : scelti.filter(x => x !== c.whatsapp_id))}
                label={(
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{c.name || c.whatsapp_id.split('@')[0]}</span>
                    {c.phone && <span className="text-xs tabular-nums text-muted">{c.phone}</span>}
                    {!c.phone && conNumeri && <Badge tone="warning">Numero non disponibile</Badge>}
                    {c.origin === 'mittente' && <Badge>Scrive in un gruppo</Badge>}
                  </span>
                )}
              />
            </li>
          ))}
          {visibili.length > MOSTRATI && (
            <li className="px-3 py-2 text-xs text-muted">
              Mostrati {MOSTRATI} di {visibili.length}: restringi con la ricerca.
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
